import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  BarChart3,
  ChevronRight,
  MapPin,
  PlusCircle,
  User,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, ownDancerIdParam } from "../lib/api";
import {
  API_CACHE_TTL,
  cachedGet,
  readApiCache,
} from "../lib/apiCache";
import { useAuth } from "../lib/auth";
import { useDevSession } from "../lib/devSession";
import { useGuestIdentity } from "../lib/guestIdentity";
import { earlyBirdTermsLabel } from "../lib/format";
import { PricePill } from "../components/ui/public/PricePill";
import { eventPublicPath } from "../lib/eventLinks";
import { attendanceLabel } from "../lib/rsvp";
import { eventStatusLabel } from "../lib/eventStatus";
import { hostCtaTarget } from "../lib/hostProfile";
import { isDancerProfileIncomplete } from "../lib/syncGuestProfile";
import { EmptyState, CountBadge } from "../components/ui";
import { readableTabClass } from "../components/ui/TabButton";
import { ReadableCard, ReadableNavButton, navActionClass } from "../components/Sticker";
import CheckInButton, { checkInAndGoToFloor } from "../components/CheckInButton";
import FeedGoingPreview from "../components/feed/FeedGoingPreview";
import FeedMySchools from "../components/feed/FeedMySchools";
import FeedStickyBar from "../components/feed/FeedStickyBar";
import ClassFeedCard from "../components/feed/ClassFeedCard";
import BrandLink from "../components/BrandLink";
import { activeCheckInPath, getActiveCheckInForSession, syncActiveCheckInFromFeed, clearActiveCheckIn, isLiveBannerSettled, markLiveBannerSettled } from "../lib/activeCheckIn";
import {
  applyRecentCheckoutToFeed,
  clearFeedForceRefresh,
  checkInErrorMessage,
  reconcileRecentCheckoutWithFeed,
  shouldForceFeedRefresh,
} from "../lib/checkIn";
import {
  CREW_NUDGE_TOOLTIP_KEY,
  skipFavoriteDances,
} from "../games/favoriteDances";
import {
  CrewNudgeLine,
  CrewSignalFeedCard,
  FavoriteDancesSheet,
  FavoriteDancesVoteCard,
} from "../games/favoriteDances";
import PriorityBroadcastCard from "../components/broadcasts/PriorityBroadcastCard";
import NotificationsTab from "../components/notifications/NotificationsTab";
import FavouriteRsvpBanner from "../components/notifications/FavouriteRsvpBanner";
import HostOnboardingResumeBanner from "../components/onboarding/HostOnboardingResumeBanner";
import { getUnreadNotifications, markNotificationReadLocal } from "../lib/notificationReads";
import { filterUndismissedFavouritePending } from "../lib/favouriteRsvpDismiss";
import { pickPriorityBroadcast } from "../lib/broadcastSort";

const RECURRING_LABELS = {
  weekly: "Every week",
  biweekly: "Every 2 weeks",
  monthly: "Monthly",
};

function fmtOccurrence(iso) {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/** UTC YYYY-MM-DD — matches feed occurrence_date keys. */
function utcDateKey(d = new Date()) {
  return d.toISOString().slice(0, 10);
}

function addUtcDays(dateKey, days) {
  const d = new Date(`${dateKey}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** True when occurrence is after the next `days` calendar days (today inclusive). */
function isBeyondNextDays(occurrenceDate, days = 3, now = new Date()) {
  if (!occurrenceDate) return true;
  const cutoff = addUtcDays(utcDateKey(now), days - 1);
  return occurrenceDate > cutoff;
}

function feedSecondaryMeta(item, counts, recurringLabel, hasGoingPreview) {
  const parts = [];
  if (!hasGoingPreview) parts.push(`${counts.total} going`);
  if (item.is_favourite) parts.push("Following");
  if (recurringLabel) parts.push(recurringLabel);
  if (item.event_status && item.event_status !== "confirmed") {
    parts.push(eventStatusLabel(item.event_status));
  }
  return parts.join(" · ");
}

function feedEntryKey(item) {
  if (!item) return "";
  if (item.id) return item.id;
  return `${item.event_id || ""}:${item.occurrence_date || ""}`;
}

function feedEntryKeySet(items = []) {
  return new Set(items.map(feedEntryKey).filter(Boolean));
}

function feedFooterStatus(item, past) {
  if (!item.my_rsvp) return null;
  const status = item.my_rsvp.attendance_status || "likely";
  if (past) {
    const parts = [attendanceLabel(status)];
    const role = item.my_rsvp.role;
    if (role) {
      parts.push(role.charAt(0).toUpperCase() + role.slice(1).toLowerCase());
    }
    return parts.join(" · ");
  }
  const statusLabel = status === "likely"
    ? "Going"
    : status === "maybe"
      ? "Maybe"
      : "Can't make it";
  const role = item.my_rsvp.role;
  if (!role) return statusLabel;
  return `${statusLabel} · ${role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()}`;
}

function tabSubtitle(tab) {
  if (tab === "your") {
    return "Parties you've RSVP'd to, are following, or are hosting tonight.";
  }
  if (tab === "updates") return "Schedule changes and RSVP reminders for events you follow.";
  return "";
}

function parseFeedTab(param) {
  if (param === "updates") return "updates";
  return "your";
}

function FeedTabButton({ active, children, disabled = false, onClick, testId, showUnreadDot = false }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      disabled={disabled}
      className={`relative ${readableTabClass(active, { disabled })}`}
      data-testid={testId}
    >
      {children}
      {showUnreadDot && !active && (
        <span
          className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-ink border border-surface"
          aria-hidden
        />
      )}
    </button>
  );
}

function isRequestCanceled(err) {
  return err?.code === "ERR_CANCELED" || err?.name === "CanceledError";
}

function FeedLoadError({ message, onRetry, testId = "feed-load-error" }) {
  return (
    <ReadableCard className="p-4 flex flex-col gap-3 bg-surface-muted" data-testid={testId} role="alert">
      <p className="font-ui text-[14px] font-medium text-ink/80 min-w-0 break-words [overflow-wrap:anywhere]">
        {message}
      </p>
      <ReadableNavButton type="button" onClick={onRetry} className="w-full sm:w-auto">
        Try again
      </ReadableNavButton>
    </ReadableCard>
  );
}

export default function EventsFeedPage() {
  const { account, checking } = useAuth();
  const { dancerId, profile, revision, bindDancerId } = useGuestIdentity();
  const { switching: devSwitching } = useDevSession();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = parseFeedTab(searchParams.get("tab"));
  const [tab, setTab] = useState(initialTab);
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedLoadError, setFeedLoadError] = useState(null);
  const [fdSheet, setFdSheet] = useState(null);
  const [floorLiveMessage, setFloorLiveMessage] = useState("");
  const [checkInBannerItems, setCheckInBannerItems] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [favDismissTick, setFavDismissTick] = useState(0);
  const [readTick, setReadTick] = useState(0);
  const announcedFloorRef = useRef(false);

  const canViewFeed = Boolean(account);
  const priorityBroadcast = useMemo(() => {
    void readTick;
    return pickPriorityBroadcast(notifications);
  }, [notifications, readTick]);
  const hasUnreadNotifications = useMemo(() => {
    void readTick;
    return getUnreadNotifications(notifications).length > 0;
  }, [notifications, readTick]);

  const favouriteRsvpPending = useMemo(() => {
    void favDismissTick;
    return filterUndismissedFavouritePending(feed?.favourite_rsvp_pending || []);
  }, [feed?.favourite_rsvp_pending, favDismissTick]);

  const classRsvpPending = useMemo(
    () => feed?.class_rsvp_pending || [],
    [feed?.class_rsvp_pending],
  );

  const todayFavouriteBanner = useMemo(
    () => favouriteRsvpPending.find((item) => item.is_today) || null,
    [favouriteRsvpPending],
  );

  const loadNotifications = useCallback(async ({ signal, force = false } = {}) => {
    const paramDancerId = ownDancerIdParam(dancerId);
    if (!paramDancerId && !account) {
      setNotifications([]);
      return [];
    }
    try {
      const { data, meta } = await cachedGet("/notifications", {
        params: { dancer_id: paramDancerId },
        signal,
        ttlMs: API_CACHE_TTL.BROADCASTS,
        force,
        useSinceTs: true,
      });
      if (meta?.unchanged) return data.notifications || data.broadcasts || [];
      const list = data.notifications || data.broadcasts || [];
      setNotifications(list);
      return list;
    } catch (err) {
      if (isRequestCanceled(err)) return [];
      try {
        const { data } = await cachedGet("/broadcasts", {
          params: { dancer_id: paramDancerId },
          signal,
          ttlMs: API_CACHE_TTL.BROADCASTS,
          force,
        });
        const list = data.notifications || data.broadcasts || [];
        setNotifications(list);
        return list;
      } catch (fallbackErr) {
        if (isRequestCanceled(fallbackErr)) return [];
        setNotifications([]);
        return [];
      }
    }
  }, [dancerId, account]);

  const handleNotificationRead = useCallback(async (id) => {
    markNotificationReadLocal(id);
    setReadTick((t) => t + 1);
    if (!id) return;
    if (!ownDancerIdParam(dancerId) && !account) return;
    try {
      await api.post(`/notifications/${encodeURIComponent(id)}/read`, {
        dancer_id: ownDancerIdParam(dancerId),
      });
    } catch {
      // local read state is enough for UX
    }
  }, [dancerId, account]);

  const fetchFeed = useCallback(async ({ params, signal, force = false, onCached }) => {
    if (!force) {
      const peek = await readApiCache("/feed", params);
      if (peek?.data) onCached?.(peek.data);
    }
    const { data, meta } = await cachedGet("/feed", {
      params,
      signal,
      ttlMs: API_CACHE_TTL.FEED_YOUR,
      force,
      useSinceTs: params.scope === "your",
    });
    return { data, meta };
  }, []);

  const loadGenRef = useRef(0);
  const loadRef = useRef(null);
  const spinnerScopeRef = useRef("");

  const load = useCallback(async ({ signal, force = false } = {}) => {
    const loadGen = ++loadGenRef.current;
    const applyFeed = (raw, meta = {}) => {
      if (loadGen !== loadGenRef.current || signal?.aborted) return;
      const authoritative = Boolean(meta && !meta.stale && !meta.fromCache);
      if (authoritative) {
        reconcileRecentCheckoutWithFeed(raw, { authoritative: true });
      }
      const data = applyRecentCheckoutToFeed(raw);
      if (data?.dancer_id && data.dancer_id !== dancerId) {
        bindDancerId(data.dancer_id);
      }
      setFeed(data);
      syncActiveCheckInFromFeed(data, {
        dancerId: data?.dancer_id || dancerId,
        authoritative: !meta?.stale,
      });
      setCheckInBannerItems(data.check_in || []);
    };

    try {
      setFeedLoadError(null);
      const feedParams = {
        scope: "your",
        dancer_id: ownDancerIdParam(dancerId),
      };

      const feedResult = await fetchFeed({
        params: feedParams,
        signal,
        force,
        onCached: (data) => applyFeed(data, { fromCache: true, stale: false }),
      });
      if (loadGen !== loadGenRef.current || signal?.aborted) return;
      await loadNotifications({ signal, force });
      if (loadGen !== loadGenRef.current || signal?.aborted) return;
      if (!feedResult.meta?.unchanged) {
        applyFeed(feedResult.data, feedResult.meta);
      }
      if (force || !feedResult.meta?.fromCache) {
        clearFeedForceRefresh();
      }
    } catch (err) {
      if (isRequestCanceled(err)) return;
      if (loadGen !== loadGenRef.current) return;
      const message = formatApiError(err);
      setFeedLoadError(message);
      toast.error(message);
    } finally {
      if (loadGen === loadGenRef.current && !signal?.aborted) {
        setLoading(false);
      }
    }
  }, [
    dancerId,
    bindDancerId,
    loadNotifications,
    fetchFeed,
  ]);

  const retryFeedLoad = useCallback(() => {
    setLoading(true);
    setFeedLoadError(null);
    loadRef.current({ force: true });
  }, []);

  const clearStaleCheckIn = useCallback(() => {
    clearActiveCheckIn();
    setFloorLiveMessage("");
    announcedFloorRef.current = false;
    retryFeedLoad();
  }, [retryFeedLoad]);

  loadRef.current = load;

  const crewTooltipItemId = useMemo(() => {
    try {
      if (localStorage.getItem(CREW_NUDGE_TOOLTIP_KEY)) return null;
    } catch {
      return null;
    }
    if (feed?.crew_signal_pinned?.crew_signal?.show) return feed.crew_signal_pinned.id;
    const badged = (feed?.your_nights || []).find((item) => item.crew_signal?.show);
    if (badged) return badged.id;
    const firstSignal = (feed?.crew_signals || []).find((item) => item.crew_signal?.show);
    return firstSignal?.id || null;
  }, [feed?.crew_signal_pinned, feed?.your_nights, feed?.crew_signals]);

  useEffect(() => {
    if (checking || devSwitching) return;
    if (!canViewFeed) {
      nav("/login?next=/events");
      return;
    }

    const spinnerScope = `${tab}|${revision}|${account?.id ?? ""}`;
    if (spinnerScopeRef.current !== spinnerScope) {
      spinnerScopeRef.current = spinnerScope;
      setLoading(true);
      setFeedLoadError(null);
    }

    // Peek only — do not consume here. Strict Mode remounts would otherwise
    // lose the force flag and fall back to a stale cached checked-in feed.
    const force = shouldForceFeedRefresh();

    const controller = new AbortController();
    loadRef.current({ signal: controller.signal, force });
    return () => controller.abort();
  }, [
    tab,
    revision,
    account?.id,
    checking,
    devSwitching,
    canViewFeed,
    nav,
  ]);

  const activeCheckIn = getActiveCheckInForSession(dancerId);
  const checkedInFeedItems = useMemo(() => {
    // Use feed.check_in whenever feed has loaded (including []). Falling back
    // on empty check_in left the sticky banner stuck after checkout.
    const buckets = feed ? (feed.check_in || []) : checkInBannerItems;
    return buckets.filter((item) => item.checked_in_at || item.my_rsvp?.checked_in_at);
  }, [feed, checkInBannerItems]);
  const votePendingKeys = useMemo(
    () => feedEntryKeySet(feed?.vote_pending),
    [feed?.vote_pending],
  );
  const bannerCheckInItems = useMemo(
    () => checkedInFeedItems.filter((item) => !votePendingKeys.has(feedEntryKey(item))),
    [checkedInFeedItems, votePendingKeys],
  );
  const isCheckedIn = Boolean(activeCheckIn) || checkedInFeedItems.length > 0;
  const replaceWelcome = bannerCheckInItems.length > 0;
  const comingUpNextItems = useMemo(() => {
    const nights = feed?.your_nights || [];
    if (!isCheckedIn) return nights;
    return nights.filter((item) => !item.is_live);
  }, [isCheckedIn, feed?.your_nights]);
  /** Index of first event beyond the next 3 days; divider renders before it when both sides exist. */
  const yourNightsHorizonIndex = useMemo(() => {
    const idx = comingUpNextItems.findIndex((item) => isBeyondNextDays(item.occurrence_date));
    return idx > 0 ? idx : -1;
  }, [comingUpNextItems]);
  const staleCheckIn = Boolean(
    tab === "your" && activeCheckIn && !loading && feed && checkedInFeedItems.length === 0
  );

  useEffect(() => {
    if (!replaceWelcome) {
      announcedFloorRef.current = false;
      setFloorLiveMessage("");
      return;
    }

    if (!bannerCheckInItems.length) {
      if (loading && !announcedFloorRef.current) {
        setFloorLiveMessage("Checking your check-in…");
      } else if (staleCheckIn && !announcedFloorRef.current) {
        setFloorLiveMessage("Check-in status unavailable.");
        announcedFloorRef.current = true;
      }
      return;
    }

    if (announcedFloorRef.current) return;

    const eventName = bannerCheckInItems[0]?.event?.name?.trim();
    setFloorLiveMessage(
      eventName
        ? `Checked in at ${eventName}. Tap the banner to return to the floor.`
        : "Checked in tonight. Tap the banner to return to the floor."
    );
    announcedFloorRef.current = true;
  }, [replaceWelcome, bannerCheckInItems, loading, staleCheckIn]);

  if (checking || devSwitching || !canViewFeed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas">
        <p className="font-ui font-medium text-[15px] text-ink/70">Loading your feed…</p>
      </div>
    );
  }

  const greetingName = profile?.name || account?.name;
  const greeting = greetingName ? `Hey ${greetingName.split(" ")[0]}` : "Your nights";
  const hostCta = hostCtaTarget(account);
  const profileIncomplete = isDancerProfileIncomplete(profile);

  return (
    <div className="min-h-screen bg-canvas text-ink font-body pb-[calc(4.75rem+env(safe-area-inset-bottom,0px))] sm:pb-20">
      <header className="max-w-page mx-auto px-4 pt-5 flex items-center justify-between gap-3">
        <BrandLink to="/events" variant="readable" size="md" />
        <div className="flex items-center gap-2 shrink-0">
          <ReadableNavButton
            as={Link}
            to="/me"
            className={`${navActionClass} sm:gap-1.5 ${
              profileIncomplete
                ? "profile-complete-pill border-ink/20 bg-ink/[0.04] text-ink"
                : ""
            }`}
            aria-label={profileIncomplete ? "Complete your profile" : "Your profile"}
            data-testid="nav-profile-link"
          >
            <User className="w-4 h-4" strokeWidth={2.5} aria-hidden />
            <span className="hidden sm:inline">
              {profileIncomplete ? "Complete profile" : "Profile"}
            </span>
          </ReadableNavButton>
          <ReadableNavButton
            variant="primary"
            className={navActionClass}
            data-testid="nav-create-btn"
            aria-label="Host something fun"
            onClick={() => nav(hostCta.path)}
          >
            <PlusCircle className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
            <span className="sm:hidden">Host</span>
            <span className="hidden sm:inline">Host something fun</span>
          </ReadableNavButton>
        </div>
      </header>

      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        data-testid="feed-floor-live-region"
      >
        {floorLiveMessage}
      </div>

      {staleCheckIn && (
        <section
          className="max-w-page mx-auto px-4 mt-3"
          data-testid="feed-check-in-stale"
          aria-label="Check-in status unavailable"
        >
          <ReadableCard className="p-4 flex flex-col gap-3 bg-surface-muted">
            <p className="font-ui text-[14px] font-medium text-ink/80 min-w-0 break-words [overflow-wrap:anywhere]">
              We couldn&apos;t confirm your check-in on this device. Refresh the feed, or clear check-in status if you already left the venue.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <ReadableNavButton
                type="button"
                onClick={retryFeedLoad}
                className="w-full sm:w-auto"
                data-testid="feed-check-in-stale-refresh"
              >
                Refresh feed
              </ReadableNavButton>
              <ReadableNavButton
                type="button"
                onClick={clearStaleCheckIn}
                className="w-full sm:w-auto"
                data-testid="feed-check-in-stale-clear"
              >
                Clear check-in status
              </ReadableNavButton>
            </div>
          </ReadableCard>
        </section>
      )}

      <HostOnboardingResumeBanner account={account} />

      {priorityBroadcast && (
        <PriorityBroadcastCard
          broadcast={priorityBroadcast}
          onDismiss={handleNotificationRead}
        />
      )}

      {replaceWelcome && bannerCheckInItems.length > 0 && (
        <section
          className="max-w-page mx-auto px-4 mt-2 flex flex-col gap-2 sticky top-0 z-30 feed-check-in-banner-shell border-b border-black/[0.06] py-1 pt-[max(0.25rem,env(safe-area-inset-top,0px))] sm:static sm:z-auto sm:border-b-0 sm:py-0 sm:pt-0 sm:mt-3 sm:gap-3"
          aria-label="Checked-in event"
          data-testid="feed-check-in-banner"
        >
          {bannerCheckInItems.map((item) => (
            <FeedCard
              key={item.id}
              item={item}
              hosting={item.is_hosting}
              checkIn
              liveBanner
              activeCheckIn={activeCheckIn}
              onCheckedIn={load}
            />
          ))}
        </section>
      )}

      {!replaceWelcome && (
        <div className="max-w-page mx-auto px-4 mt-6">
          <h1 className="font-ui font-semibold text-[22px] leading-tight tracking-tight text-balance">{greeting}</h1>
          <p className="mt-1.5 font-ui font-normal text-[14px] text-ink/55 max-w-[65ch]">{tabSubtitle(tab)}</p>
          <FeedMySchools />
        </div>
      )}

      <div
        className={`max-w-page mx-auto px-4 hidden sm:flex flex-wrap gap-2 ${replaceWelcome ? "mt-3" : "mt-6"}`}
        role="tablist"
        aria-label="Feed sections"
      >
        <FeedTabButton active={tab === "your"} onClick={() => setTab("your")} testId="feed-tab-your">
          Your Events
        </FeedTabButton>
        <FeedTabButton
          active={tab === "updates"}
          disabled={!dancerId}
          onClick={() => setTab("updates")}
          testId="feed-tab-updates"
          showUnreadDot={hasUnreadNotifications}
        >
          Notifications
        </FeedTabButton>
      </div>

      {feedLoadError && !loading && (
        <div className="max-w-page mx-auto px-4 mt-4">
          <FeedLoadError message={feedLoadError} onRetry={retryFeedLoad} />
        </div>
      )}

      {loading && tab !== "updates" && !feed ? (
        <p
          className="max-w-page mx-auto px-4 mt-8 font-ui font-medium text-[15px] text-ink/70"
          role="status"
          aria-live="polite"
        >
          Loading events…
        </p>
      ) : tab === "updates" ? (
        <NotificationsTab
          notifications={notifications}
          readTick={readTick}
          dancerId={dancerId}
          onRead={handleNotificationRead}
          onReadAll={(ids) => {
            ids.forEach((id) => {
              api.post(`/notifications/${encodeURIComponent(id)}/read`, {
                dancer_id: ownDancerIdParam(dancerId),
              }).catch(() => {});
            });
            setReadTick((t) => t + 1);
          }}
        />
      ) : tab === "your" ? (
        <div className={`max-w-page mx-auto px-4 flex flex-col ${isCheckedIn ? "mt-4 gap-6" : "mt-8 gap-10"}`}>

          {todayFavouriteBanner && (
            <FavouriteRsvpBanner
              item={todayFavouriteBanner}
              onDismiss={() => setFavDismissTick((t) => t + 1)}
            />
          )}

          {classRsvpPending.length > 0 && (
            <FeedSection
              title="Your classes · RSVP?"
              subtitle="Classes you follow — lock in your spot for the next session."
              testId="feed-class-rsvp-pending"
            >
              {classRsvpPending.map((item) => (
                <ClassFeedCard key={item.id} item={item} />
              ))}
            </FeedSection>
          )}

          {feed?.vote_pending?.length > 0 && (
            <FeedSection
              title="Vote for your favourites"
              subtitle="You're checked out — celebrate the dancers who made your night."
              testId="feed-vote-pending"
            >
              {feed.vote_pending.map((item) => (
                <FavoriteDancesVoteCard
                  key={item.id}
                  item={item}
                  onVote={() => setFdSheet(item)}
                  onSkip={async () => {
                    try {
                      await skipFavoriteDances(item.event_id, {
                        dancerId,
                        occurrenceDate: item.occurrence_date,
                      });
                    } catch (err) {
                      toast.error(formatApiError(err));
                      return;
                    }
                    load({ force: true });
                  }}
                />
              ))}
            </FeedSection>
          )}

          {feed?.crew_signal_pinned && (
            <div data-testid="feed-crew-signal-pinned">
              <CrewSignalFeedCard
                item={feed.crew_signal_pinned}
                showTooltip={feed.crew_signal_pinned.id === crewTooltipItemId}
              />
            </div>
          )}

          {!isCheckedIn && feed?.check_in?.length > 0 && (
            <FeedSection
              title="Check in"
              subtitle="Tap when you arrive at the door."
              testId="feed-check-in"
            >
              {feed.check_in.map((item) => (
                <FeedCard
                  key={item.id}
                  item={item}
                  hosting={item.is_hosting}
                  checkIn
                  activeCheckIn={activeCheckIn}
                  onCheckedIn={load}
                  dancerId={dancerId}
                />
              ))}
            </FeedSection>
          )}

          {isCheckedIn && bannerCheckInItems.length > 0 && !comingUpNextItems.length && !loading && (
            <p
              className="font-ui font-medium text-[15px] text-ink/75 text-pretty text-center py-2"
              data-testid="feed-on-floor-solo"
            >
              You&apos;re checked in — nothing else on your list.
            </p>
          )}

          {(comingUpNextItems.length > 0 || !isCheckedIn) && (
          <FeedSection
            title={isCheckedIn ? "Coming up next" : "Your events"}
            testId="feed-your-nights"
            compact={isCheckedIn}
          >
            {comingUpNextItems.length ? (
              comingUpNextItems.map((item, index) => (
                <React.Fragment key={item.id}>
                  {index === yourNightsHorizonIndex && (
                    <div
                      className="flex justify-center py-1"
                      aria-hidden
                      data-testid="feed-horizon-divider"
                    >
                      <div className="w-10 h-px bg-ink/20" />
                    </div>
                  )}
                  <FeedCard
                    item={item}
                    hosting={item.is_hosting}
                    dancerId={dancerId}
                    showCrewTooltip={item.id === crewTooltipItemId}
                  />
                </React.Fragment>
              ))
            ) : (
              <EmptySection message="No upcoming events yet — RSVP from an invite link or host your own social." />
            )}
          </FeedSection>
          )}

          {feed?.crew_signals?.length > 0 && (
            <FeedSection title="Your crew is going" testId="feed-crew-signals" compact={isCheckedIn}>
              {feed.crew_signals.map((item) => (
                <CrewSignalFeedCard
                  key={item.id}
                  item={item}
                  showTooltip={item.id === crewTooltipItemId}
                />
              ))}
            </FeedSection>
          )}
        </div>
      ) : null}


      {fdSheet && dancerId && (
        <FavoriteDancesSheet
          open
          eventId={fdSheet.event_id}
          occurrenceDate={fdSheet.occurrence_date}
          dancerId={dancerId}
          onClose={() => setFdSheet(null)}
          onSubmitted={() => {
            setFdSheet(null);
            load({ force: true });
          }}
        />
      )}

      <FeedStickyBar
        tab={tab}
        hasUnreadUpdates={hasUnreadNotifications}
        onTabChange={setTab}
        dancerId={dancerId}
      />
    </div>
  );
}

function FeedSection({ title, subtitle, children, testId, action, compact = false }) {
  return (
    <section data-testid={testId} className={compact ? "feed-section-compact" : undefined}>
      <header className={`flex flex-wrap items-end justify-between gap-3 ${compact ? "mb-3" : "mb-4"}`}>
        <div>
          <h2 className={`font-ui font-semibold leading-tight text-balance ${compact ? "text-[18px]" : "text-[20px] sm:text-[22px]"}`}>{title}</h2>
          {subtitle && (
            <p className="font-ui font-medium text-ink/70 text-[14px] mt-0.5 max-w-[65ch]">{subtitle}</p>
          )}
        </div>
        {action}
      </header>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}

function EmptySection({ message }) {
  return (
    <EmptyState
      compact
      title={message}
      className="feed-empty-card"
    />
  );
}

function feedDashboardAriaLabel(pendingCount) {
  if (!pendingCount || pendingCount <= 0) return "Dashboard";
  const noun = pendingCount === 1 ? "invite" : "invites";
  return `Dashboard, ${pendingCount} pending ${noun}`;
}

function FeedCard({ item, hosting = false, past = false, checkIn = false, liveBanner = false, activeCheckIn = null, onCheckedIn, dancerId = null, showCrewTooltip = false }) {
  const nav = useNavigate();
  const location = useLocation();
  const { dancerId: sessionDancerId } = useGuestIdentity();
  const effectiveDancerId = dancerId || sessionDancerId;
  const ev = item.event;
  const counts = item.counts || { total: 0, balanced_leaders: 0, balanced_followers: 0 };
  const checkedInAt = item.checked_in_at || item.my_rsvp?.checked_in_at;
  const occurrenceKey = item.occurrence_date || item.my_rsvp?.occurrence_date || null;
  const [liveSettled, setLiveSettled] = useState(() => (
    checkedInAt ? isLiveBannerSettled(item.event_id, occurrenceKey) : true
  ));
  const [checkingIn, setCheckingIn] = useState(false);

  useEffect(() => {
    if (!checkedInAt || liveSettled) return undefined;
    const timer = window.setTimeout(() => {
      markLiveBannerSettled(item.event_id, occurrenceKey);
      setLiveSettled(true);
    }, 3800);
    return () => window.clearTimeout(timer);
  }, [checkedInAt, item.event_id, occurrenceKey, liveSettled]);

  if (!ev) return null;

  const earlyBirdTerms = earlyBirdTermsLabel(ev.pricing, { rsvpCount: counts.total });
  const recurringLabel = item.recurring ? RECURRING_LABELS[item.recurring] || item.recurring : null;
  const publicPath = ev.public_path || eventPublicPath(ev);
  const eventPath = publicPath
    ? `${publicPath}${item.occurrence_date ? `?occurrence_date=${item.occurrence_date}` : ""}`
    : "/events";
  const floorPath = checkedInAt
    ? activeCheckInPath({
      event_id: item.event_id,
      occurrence_date: item.occurrence_date || item.my_rsvp?.occurrence_date,
      public_path: ev.public_path,
    })
    : null;
  const cardPath = floorPath || eventPath;
  const isActiveFloor = Boolean(
    checkedInAt
    || (activeCheckIn
      && activeCheckIn.event_id === item.event_id
      && (!activeCheckIn.occurrence_date || activeCheckIn.occurrence_date === item.occurrence_date))
  );
  const showCheckInButton = checkIn && item.can_check_in && !isActiveFloor && !activeCheckIn;
  const showUpdateRsvp = showCheckInButton && Boolean(item.my_rsvp);
  const showHostingAction = hosting;
  const showRsvpButton = !past && !checkedInAt && !item.my_rsvp && !showHostingAction && !showCheckInButton;
  const footerStatus = feedFooterStatus(item, past);
  const showFooterStatus = footerStatus && !checkedInAt;
  const hasFooterAction = !checkedInAt && (showCheckInButton || showUpdateRsvp || showHostingAction || showRsvpButton);
  const isLiveBanner = Boolean(liveBanner && checkedInAt && floorPath);
  const goingPreview = item.going_preview || [];
  const hasGoingPreview = goingPreview.length > 0 && counts.total > 0;
  const secondaryMeta = feedSecondaryMeta(item, counts, recurringLabel, hasGoingPreview);

  const liveBannerContent = (
    <div className="flex items-center gap-3 min-w-0">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span
            className="inline-flex items-center rounded-md2 bg-white/20 border border-white/30 px-2 py-0.5 font-ui font-semibold text-[11px] text-cream shrink-0"
            data-testid="feed-checked-in-tag"
            id={`feed-checked-in-tag-${item.id}`}
          >
            On the floor
          </span>
          <span className="font-ui font-medium text-[12px] text-cream/85 tabular-nums shrink-0">
            {fmtOccurrence(item.occurrence_at)}
          </span>
        </div>
        <h3 className="mt-1 font-ui font-semibold text-[17px] sm:text-[18px] leading-snug text-balance break-words [overflow-wrap:anywhere] line-clamp-2">
          {ev.name}
        </h3>
      </div>
      <ChevronRight
        className="w-5 h-5 shrink-0 text-cream/90 feed-live-banner-chevron"
        strokeWidth={2.5}
        aria-hidden
      />
    </div>
  );

  const identityContent = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={`font-ui font-medium text-[12px] sm:text-[13px] tracking-wide tabular-nums ${checkedInAt ? "text-cream/85" : "text-ink/50"}`}>
            {fmtOccurrence(item.occurrence_at)}
            {item.is_today && !checkedInAt ? " · Today" : ""}
          </p>
          {checkedInAt && (
            <span
              className="mt-1 inline-flex items-center rounded-btn bg-white/20 border border-white/30 px-2 py-0.5 font-ui font-medium text-[12px] text-cream"
              data-testid="feed-checked-in-tag"
              id={`feed-checked-in-tag-${item.id}`}
            >
              On the floor
            </span>
          )}
          <h3 className={`font-ui font-semibold leading-snug text-balance break-words [overflow-wrap:anywhere] ${
            checkedInAt
              ? "mt-1.5 text-[17px] sm:text-[18px]"
              : "mt-1 text-[15px] sm:text-[16px] text-ink"
          }`}>{ev.name}</h3>
        </div>
        {!checkedInAt && (
          <PricePill
            pricing={ev.pricing}
            rsvpCount={counts.total}
            className="shrink-0"
          />
        )}
      </div>
      {secondaryMeta && !checkedInAt && (
        <p className="mt-1.5 font-ui text-[12px] font-medium text-ink/50 min-w-0 break-words [overflow-wrap:anywhere]">
          {secondaryMeta}
        </p>
      )}
      {!checkedInAt && (
        <p className="mt-2 text-[12px] sm:text-[13px] font-ui font-medium inline-flex items-start gap-1.5 break-words [overflow-wrap:anywhere] text-ink/50">
          <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5 opacity-70" strokeWidth={2} aria-hidden />
          {ev.location}{ev.city ? `, ${ev.city}` : ""}
        </p>
      )}
    </>
  );

  async function handleCheckInCardTap(e) {
    e.preventDefault();
    if (checkingIn) return;
    setCheckingIn(true);
    try {
      await checkInAndGoToFloor({
        eventId: item.event_id,
        occurrenceDate: item.occurrence_date,
        publicPath: publicPath || ev.public_path,
        dancerId: effectiveDancerId,
        navigate: nav,
        from: `${location.pathname}${location.search}`,
        onCheckedIn,
      });
    } catch (err) {
      toast.error(checkInErrorMessage(err));
    } finally {
      setCheckingIn(false);
    }
  }

  const hasFooter = !checkedInAt && (showFooterStatus || hasFooterAction);

  return (
    <ReadableCard
      className={`p-0 overflow-hidden relative group quiet-card-interactive${
        checkedInAt ? ` feed-card-live${liveSettled ? " feed-card-live--settled" : ""}${isLiveBanner ? " feed-card-live--banner" : ""}` : ""
      }`}
      data-testid={`feed-card-${item.id}`}
    >
      {isLiveBanner ? (
        <Link
          to={floorPath}
          className="feed-live-banner-link check-in-gradient text-cream relative overflow-hidden block p-3.5 sm:p-4 rounded-[inherit] focus-visible:outline focus-visible:outline-2 focus-visible:outline-cream/50 focus-visible:outline-offset-2 touch-manipulation active:brightness-[0.98]"
          aria-label={`Open the floor for ${ev.name}`}
          aria-describedby={`feed-checked-in-tag-${item.id}`}
          data-testid={`feed-view-${item.id}`}
        >
          <span className="relative z-[1] block">{liveBannerContent}</span>
        </Link>
      ) : (
      <>
      {showCheckInButton ? (
        <button
          type="button"
          onClick={handleCheckInCardTap}
          disabled={checkingIn}
          aria-busy={checkingIn || undefined}
          className="absolute inset-0 z-10 rounded-[inherit] cursor-pointer disabled:cursor-wait"
          aria-label={`Check in to ${ev.name}`}
          data-testid={`feed-view-${item.id}`}
        />
      ) : (
        <Link
          to={cardPath}
          className="absolute inset-0 z-10 rounded-[inherit]"
          aria-label={`${past ? "View" : "RSVP to"} ${ev.name}`}
          data-testid={`feed-view-${item.id}`}
        />
      )}
      {/* Quiet DemoLandingCard body — cover wash only for live check-in (intentional loud). */}
      <div
        className={`${
          checkedInAt
            ? "check-in-gradient text-cream relative overflow-hidden [&>*]:relative [&>*]:z-[1] p-4 sm:p-5"
            : hasFooter
              ? "bg-surface px-4 pt-4 pb-2"
              : "bg-surface p-4"
        }`}
      >
        {identityContent}
        {!checkedInAt && item.crew_signal?.show && (
          <CrewNudgeLine show showTooltip={showCrewTooltip} tooltipContext="main-feed" />
        )}
        {hasGoingPreview && !checkedInAt && (
          <FeedGoingPreview
            preview={goingPreview}
            total={counts.total}
            className="mt-3"
          />
        )}
        {earlyBirdTerms && !checkedInAt && (
          <p className="mt-1.5 text-[12px] font-ui font-medium text-ink/50" data-testid="feed-early-bird-note">
            {earlyBirdTerms}
          </p>
        )}
        {hasFooter && (
          <div className="mt-3 flex items-center justify-between gap-3 relative z-20">
            {showFooterStatus ? (
              <p className="font-ui text-[13px] font-medium text-ink/55 min-w-0">{footerStatus}</p>
            ) : (
              <span />
            )}
            {hasFooterAction && (
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                {showUpdateRsvp && (
                  <ReadableNavButton
                    as={Link}
                    to={eventPath}
                    className="min-h-0 py-1.5 px-3 text-[13px] relative z-20"
                    data-testid={`feed-update-rsvp-${item.id}`}
                  >
                    Update RSVP
                  </ReadableNavButton>
                )}
                {showCheckInButton && (
                  <CheckInButton
                    eventId={item.event_id}
                    occurrenceDate={item.occurrence_date}
                    publicPath={publicPath || ev.public_path}
                    checkedInAt={null}
                    onCheckedIn={onCheckedIn}
                    size="sm"
                    testId={`feed-check-in-${item.id}`}
                  />
                )}
                {showHostingAction && (
                  <ReadableNavButton
                    as={Link}
                    to={`/dashboard/${ev.id}`}
                    className="min-h-0 py-1.5 px-3 text-[13px]"
                    aria-label={feedDashboardAriaLabel(item.pending_invite_count)}
                    data-testid={`feed-dashboard-${ev.id}`}
                  >
                    <BarChart3 className="w-4 h-4" strokeWidth={2} /> Dashboard
                    <CountBadge count={item.pending_invite_count} className="ml-0.5" />
                  </ReadableNavButton>
                )}
                {showRsvpButton && (
                  <span
                    className="relative z-20 inline-block font-ui text-[13px] font-medium text-ink/70 underline decoration-2 underline-offset-[4px] decoration-ink/40 group-hover:text-ink group-hover:decoration-ink/70 transition-colors"
                    data-testid={`feed-rsvp-${item.id}`}
                    aria-hidden
                  >
                    RSVP
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      </>
      )}
    </ReadableCard>
  );
}
